import { Button, Card, CardContent, cn } from '@repo/ui'
import { Link, useLocation } from '@tanstack/react-router'
import React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import ChevronRightIcon from '@/assets/icons/chevron-right.svg?react'
import { router } from '@/router'
import { useUIStore } from '@/store/ui-store'

type Props = {
  title: string
  subtitle: string
  hideBackButton?: boolean
  ctaButton?: React.ReactNode
  titleBadge?: React.ReactNode
  /** Denser title card for data-heavy pages (e.g. DDD viewer). */
  compact?: boolean
}

function PageHeader({
  title,
  subtitle,
  hideBackButton,
  ctaButton,
  titleBadge,
  compact = false,
}: Props) {
  const { t } = useTranslation('common')
  const { pathname } = useLocation()

  const segments = pathname.split('/').filter(Boolean)
  const isNested = segments[0] === 'dashboard' && segments.length > 2
  const parentSegment = isNested ? segments[1] : null
  const parentKey = parentSegment?.replace(/-([a-z])/g, (_, c: string) =>
    c.toUpperCase(),
  )
  const parentLabel = parentKey
    ? (t(`pageTitles.${parentKey}` as never) as string)
    : null
  const parentHref = parentSegment ? `/dashboard/${parentSegment}` : null

  const handleButtonClick = () => {
    router.history.back()
  }

  const showBreadcrumb = isNested && parentHref && parentLabel
  const headerSlot = useUIStore((s) => s.headerSlot)

  return (
    <div className="flex flex-col gap-3">
      {showBreadcrumb && (
        <>
          {/* Desktop: inline in the app header; mobile: own card (no room). */}
          {headerSlot
            ? createPortal(
                <Breadcrumb
                  parentHref={parentHref}
                  parentLabel={parentLabel}
                  title={title}
                  onBack={hideBackButton ? undefined : handleButtonClick}
                  backLabel={t('back')}
                  inline
                />,
                headerSlot,
              )
            : null}
          <Card className={cn('py-0', headerSlot && 'md:hidden')}>
            <CardContent className="py-2.5 sm:py-2.5 sm:pr-4 sm:pl-3">
              <Breadcrumb
                parentHref={parentHref}
                parentLabel={parentLabel}
                title={title}
                onBack={hideBackButton ? undefined : handleButtonClick}
                backLabel={t('back')}
              />
            </CardContent>
          </Card>
        </>
      )}

      <Card className={cn(compact && 'py-3')}>
        <CardContent
          className={cn(
            'flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:py-0',
            compact ? 'sm:pr-4 sm:pl-5' : 'sm:pr-6 sm:pl-[26px]',
          )}
        >
          {compact ? (
            <div className="flex w-full min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-1 sm:flex-nowrap">
              <h2 className="text-secondary shrink-0 text-lg leading-tight font-bold md:text-xl">
                {title}
              </h2>
              {titleBadge}
              <p
                className="text-muted-foreground min-w-0 basis-full truncate text-xs sm:basis-auto md:text-sm"
                title={subtitle}
              >
                {subtitle}
              </p>
            </div>
          ) : (
            <div className="flex min-w-0 flex-col">
              <div className="flex items-center gap-2">
                <h2 className="text-secondary text-2xl font-bold md:text-3xl">
                  {title}
                </h2>
                {titleBadge}
              </div>
              <p className="text-muted-foreground text-sm md:text-base">
                {subtitle}
              </p>
            </div>
          )}

          {(ctaButton || (!hideBackButton && !showBreadcrumb)) && (
            <div className="flex w-full flex-col items-stretch gap-3 sm:ml-auto sm:w-auto sm:flex-row sm:items-center sm:gap-4">
              {!hideBackButton && !ctaButton && !showBreadcrumb && (
                <Button
                  variant="outline"
                  onClick={handleButtonClick}
                  className="w-full sm:w-auto"
                >
                  <span className="flex items-center gap-2">
                    <ChevronRightIcon className="size-4 rotate-180" />{' '}
                    {t('back')}
                  </span>
                </Button>
              )}
              {ctaButton}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function Breadcrumb({
  parentHref,
  parentLabel,
  title,
  onBack,
  backLabel,
  inline = false,
}: {
  parentHref: string
  parentLabel: string
  title: string
  onBack?: () => void
  backLabel: string
  /** Rendered inside the app header (denser). */
  inline?: boolean
}) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      {onBack && (
        <Button
          variant="outline"
          size="sm"
          onClick={onBack}
          className={cn('shrink-0 px-3', inline ? 'h-8' : 'h-9')}
        >
          <span className="flex items-center gap-1.5">
            <ChevronRightIcon className="size-4 rotate-180" />
            {backLabel}
          </span>
        </Button>
      )}
      <nav className="text-muted-foreground flex min-w-0 items-center gap-2 text-xs sm:text-sm">
        <Link
          to={parentHref}
          className="hover:text-foreground shrink-0 transition-colors"
        >
          {parentLabel}
        </Link>
        <span className="text-muted-foreground/60">/</span>
        <span className="text-foreground min-w-0 truncate font-semibold">
          {title}
        </span>
      </nav>
    </div>
  )
}

export default PageHeader
